import { defineStore } from 'pinia'
import { addHours } from 'date-fns'
import {
  getCSRFToken,
  scrollToElement,
  getQueryParam,
  getTimezoneOffsetInHours,
  isNetworkDisconnect,
  deleteQueryParam,
  setQueryParam,
} from 'www/utils'
import { APPOINTMENT_TYPES } from 'www/constants'
import LocationParser from 'www/modules/LocationParser'
import {
  APPOINTMENT_YANDEX_GOALS,
  CLUB_YANDEX_GOALS,
  TELEMED_YANDEX_GOALS,
  TOWN_ONLINE_GOALS,
} from 'www/yandexGoals'
import { requestNewAppointment } from 'www/vue_components/common/AppointmentPage/api'
import {
  getAppointmentRegistrationTypes,
  getAppointmentObjectTypes,
  getInitAdditionalData,
  getAppointmentCallTimes,
  getAppointmentCall,
} from 'www/vue_components/common/AppointmentPage/interfaces'
import {
  APPOINTMENT_ERRORS,
  APPOINTMENT_TYPE_NAMES,
  APPOINTMENT_PLACE_TYPES,
  USER_ACTION_AFTER_AUTH,
  NEW_APPOINTMENT_URLS,
  PRELOADER_DELAY_FOR_TELEMED_MS,
  APPOINTMENT_TYPE_LABELS,
} from 'www/vue_components/common/AppointmentPage/constants'
import {
  getSuccessCallTimeTitle,
  getRemainingTimeMessage,
} from 'www/vue_components/common/AppointmentPage/functions'
import { PAGE_SOURCES } from 'www/modules/LocationParser/constants'
// eslint-disable-next-line import/no-cycle
import useUserDataStore from 'www/vue_components/common/AppointmentPage/stores/useUserDataStore'
// eslint-disable-next-line import/no-cycle
import useClubDataStore from 'www/vue_components/common/AppointmentPage/stores/useClubDataStore'
import useGlobalDataStore from 'www/vue_components/common/AppointmentPage/stores/useGlobalDataStore'
// eslint-disable-next-line import/no-cycle
import useDoctorDataStore from 'www/vue_components/common/AppointmentPage/stores/useDoctorDataStore'
// eslint-disable-next-line import/no-cycle
import useServiceDataStore from 'www/vue_components/common/AppointmentPage/stores/useServiceDataStore'
// eslint-disable-next-line import/no-cycle
import useYandexGoalsStore from 'www/vue_components/common/AppointmentPage/stores/useYandexGoalsStore'
// eslint-disable-next-line import/no-cycle
import useCalendarDataStore from 'www/vue_components/common/AppointmentPage/stores/useCalendarDataStore'
import useAuthorizationStore from 'www/vue_components/common/AppointmentPage/stores/useAuthorizationStore' // todo(useAuthMt) - удалить
// eslint-disable-next-line import/no-cycle
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
// eslint-disable-next-line import/no-cycle
import usePreliminaryConsultationStore from 'www/vue_components/common/AppointmentPage/stores/usePreliminaryConsultationStore'
import useLpuDataStore from 'www/vue_components/common/AppointmentPage/stores/useLpuDataStore'
import useAttachedFilesStore from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/stores/useAttachedFilesStore'

const locationParserReferrer = new LocationParser({
  testedLocation: 'referrer',
})

const useAppointmentDataStore = defineStore('appointmentDataStore', {
  state: () => ({
    appointmentRegistrationTypes: getAppointmentRegistrationTypes(),
    appointmentObjectTypes: getAppointmentObjectTypes(),
    initAdditionalData: getInitAdditionalData(),
    selectedLpuCallType: 'lpu_choice',
    appointmentCallTimes: getAppointmentCallTimes(),
    appointmentCall: getAppointmentCall(),
    selectedAppointmentCallTime: null,
    startDateAppointment: null,
    appointmentNode: null,
    isAppointmentByReferral: false,
  }),
  getters: {
    isDontWorkLpuWithExpress() {
      const { lpuData } = useLpuDataStore()

      return this.appointmentRegistrationTypes.isExpress
        && lpuData.nearestAppointmentExpress.length > 3 // Ожидается такая строка - 22 августа с 8:00. Если ничего не будет передано с backend - в строке будет ` с ` (смотри форматирование на фронте с помощью date)
    },
    useAuthMt() {
      return !window.IS_YDOC
    },
    appointmentPlaceTypes() {
      return {
        isLpu: !this.initAdditionalData.placeType,
        isHome: this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.home,
        isOnline: this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.online,
        isTelemed: this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.telemed,
        isTelemedAndTownOnline: (
          this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.telemed
          && getQueryParam('source') === PAGE_SOURCES.doctorListOnline[0]
        ),
        isClub: this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.club,
        isTelemedMedtochka: this.initAdditionalData.placeType === APPOINTMENT_PLACE_TYPES.telemedMedtochka,
      }
    },
    isAnyTelemed() {
      return this.appointmentPlaceTypes.isTelemed || this.appointmentPlaceTypes.isTelemedMedtochka
    },
    appointmentTypeName() {
      if (this.appointmentRegistrationTypes.isDirect) {
        return APPOINTMENT_TYPE_NAMES.direct
      }

      if (this.appointmentRegistrationTypes.isExpress) {
        return APPOINTMENT_TYPE_NAMES.express
      }
    },
    appointmentTypeLabel() {
      return APPOINTMENT_TYPE_LABELS[this.appointmentTypeName]
    },
    isShowPhoneOnly() {
      const doctorDataStore = useDoctorDataStore()
      const serviceDataStore = useServiceDataStore()

      if (this.appointmentObjectTypes.isDoctor) {
        return doctorDataStore.selectedWorkplace.isShowPhoneOnly
      }

      if (this.appointmentObjectTypes.isServices) {
        return serviceDataStore.serviceData.isShowPhoneOnly
      }
    },
    appointmentCallTimeValidationRules() {
      return [val => !!val || 'Выберите время звонка']
    },
    isValidAppointmentCallTime() {
      if (!this.appointmentCallTimes.times.length) {
        return true
      }

      return this.appointmentCallTimeValidationRules.every(
        validation => validation(this.selectedAppointmentCallTime) === true,
      )
    },
    isClubPossibleOnPage() {
      return this.appointmentRegistrationTypes.isDirect
        && (this.appointmentPlaceTypes.isLpu || this.appointmentPlaceTypes.isClub)
    },
    availableAppointmentCallTimes() {
      const calendarDataStore = useCalendarDataStore()

      const { times: callTimes, minHoursToCall } = this.appointmentCallTimes

      if (calendarDataStore.isLoadingSlots) {
        return []
      }

      if (!calendarDataStore.isAvailableSlot && calendarDataStore.isEmptyCalendarList) {
        return callTimes
      }

      if (!callTimes.length || !calendarDataStore.isAvailableSlot) {
        return []
      }

      const minDateTimeToCall = addHours(calendarDataStore.selectedSlotDate, -minHoursToCall)
      const firstUnavailableTime = callTimes.findIndex(
        ({ date }) => new Date(date) > minDateTimeToCall,
      )

      if (firstUnavailableTime === -1) {
        return callTimes
      }

      return callTimes.slice(0, firstUnavailableTime)
    },
    isTelemedPaymentInfoVisible() {
      return this.appointmentRegistrationTypes.isDirect
        && this.appointmentObjectTypes.isDoctor
        && (this.appointmentPlaceTypes.isTelemed || this.appointmentPlaceTypes.isTelemedMedtochka)
    },
    isAppointmentPaymentInfoVisible() {
      const clubDataStore = useClubDataStore()

      return clubDataStore.isClubSelectTypePaymentVisible
        || clubDataStore.isClubAvailable
        || this.isTelemedPaymentInfoVisible
    },
    useServiceExpress() {
      const preliminaryConsultationStore = usePreliminaryConsultationStore()

      return preliminaryConsultationStore.useServiceExpressWithoutConsultation
    },
    /**
     * ОЗ на услугу недоступна (пустой schedule), нужна заглушка или телефон.
     */
    isServiceEmptySlotsUnavailable() {
      const calendarDataStore = useCalendarDataStore()

      if (!this.appointmentObjectTypes.isServices) {
        return false
      }

      if (!this.appointmentRegistrationTypes.isDirect) {
        return false
      }

      return !calendarDataStore.isZeroRequests
        && !calendarDataStore.isLoadingSlots
        && calendarDataStore.isEmptyCalendarList
        && !calendarDataStore.editableCalendar.selectedDate.isSlotFixed
    },
  },
  actions: {
    /**
     * Если ОЗ на услугу недоступна: при ЭЗ клиники — экран с телефоном, иначе заглушка.
     *
     * Срабатывает при пустом schedule и при fallback первичной консультации (`useServiceExpress`).
     *
     * @param {object} [opts] -
     * @param {boolean} [opts.forceEmptySlots] - ответ schedule уже пустой (внутри then, до finally)
     */
    applyServiceEmptySlotsFallback({ forceEmptySlots = false } = {}) {
      if (!this.appointmentObjectTypes.isServices) {
        return false
      }

      const canApplyFallback = forceEmptySlots
        || this.isServiceEmptySlotsUnavailable
        || this.useServiceExpress

      if (!canApplyFallback) {
        return false
      }

      // Уже в phone-only / express без ОЗ — не трогаем
      if (!this.appointmentRegistrationTypes.isDirect && !this.useServiceExpress) {
        return false
      }

      const lpuDataStore = useLpuDataStore()
      const serviceDataStore = useServiceDataStore()
      const globalDataStore = useGlobalDataStore()
      const { hasAppointmentServiceExpress, phone } = lpuDataStore.lpuData

      if (hasAppointmentServiceExpress && phone) {
        this.appointmentRegistrationTypes.isDirect = false
        this.appointmentRegistrationTypes.isExpress = true
        serviceDataStore.serviceData = {
          ...serviceDataStore.serviceData,
          isShowPhoneOnly: true,
        }

        return true
      }

      globalDataStore.updateErrorData({
        isEnabled: true,
        isDialogRender: false,
        isPersistent: true,
        ...APPOINTMENT_ERRORS.appointmentServiceOnlineNotWork,
      })

      return true
    },
    updateStartDateAppointment(payload) {
      this.startDateAppointment = payload
    },
    updateAppointmentCall(payload) {
      this.appointmentCall = payload
    },
    updateSelectedAppointmentCallTime(payload) {
      this.selectedAppointmentCallTime = payload
    },
    updateAppointmentNode(payload) {
      this.appointmentNode = payload
    },
    updateInitAdditionalPlaceType(payload) {
      if (payload === APPOINTMENT_TYPES.lpu) {
        deleteQueryParam('type')
      } else {
        setQueryParam('type', payload)
      }

      this.initAdditionalData.placeType = payload
    },

    async validateAppointmentData() {
      const doctorDataStore = useDoctorDataStore()
      const userDataStore = useUserDataStore()
      const calendarDataStore = useCalendarDataStore()
      const familyProfilesStore = useFamilyProfilesStore()

      // Порядок вызова важен
      const validationMethods = [
        familyProfilesStore.validatePatientCard(),
        userDataStore.validateWarningInfoTelemed(),
        userDataStore.validateCheckboxes(),
      ]

      if (!this.appointmentRegistrationTypes.isExpress) {
        validationMethods.unshift(calendarDataStore.validateCalendar())
      }

      if (this.appointmentObjectTypes.isDoctor) {
        validationMethods.push(doctorDataStore.validateSelectedSpeciality())
      }

      const validationResults = await Promise.all(validationMethods)
      const scrollElement = validationResults.find(el => el)

      if (scrollElement?.blockSubmitWithoutScroll) {
        return false
      }

      if (scrollElement) {
        scrollToElement(scrollElement)
      }

      return !scrollElement
    },
    async confirmAppointment() {
      const isValid = await this.validateAppointmentData()

      if (this.isAppointmentByReferral) {
        const attachedFilesStore = useAttachedFilesStore()
        const isValidAttachedFiles = attachedFilesStore.validate()

        if (!isValid || !isValidAttachedFiles) {
          return
        }
      } else if (!isValid) {
        return
      }

      scrollToElement(this.appointmentNode)

      const clubDataStore = useClubDataStore()

      clubDataStore.updateStateIsClubModalVisible(this.appointmentPlaceTypes.isClub)

      this.sendAppointmentData()
    },
    appointmentUserPhone(userPhone) {
      if (window.IS_YDOC) {
        const { code } = window.COUNTRY_DATA.phone
        const codeWithoutPlus = code[0] === '+' ? code.slice(1) : code
        const userPhoneWithoutPlus = userPhone[0] === '+' ? userPhone.slice(1) : userPhone

        return userPhoneWithoutPlus && userPhoneWithoutPlus.slice(0, codeWithoutPlus.length) === codeWithoutPlus
          ? userPhoneWithoutPlus.slice(codeWithoutPlus.length)
          : userPhoneWithoutPlus
      }

      return userPhone && userPhone[0] === '7' ? userPhone.slice(1) : userPhone
    },
    sendAppointmentData() {
      const clubDataStore = useClubDataStore()
      const userDataStore = useUserDataStore()
      const doctorDataStore = useDoctorDataStore()
      const serviceDataStore = useServiceDataStore()
      const yandexGoalsStore = useYandexGoalsStore()
      const calendarDataStore = useCalendarDataStore()
      const authorizationStore = useAuthorizationStore() // todo(useAuthMt) - удалить
      const familyProfilesStore = useFamilyProfilesStore()
      const appointmentDataStore = useAppointmentDataStore()
      const preliminaryConsultationStore = usePreliminaryConsultationStore()

      const { selectedPatient } = familyProfilesStore
      const { placeType } = appointmentDataStore.initAdditionalData
      const { userPhone } = authorizationStore // todo(useAuthMt) - удалить
      const { userPhone: userPhoneV2 } = userDataStore // todo(useAuthMt) - переименовать поле
      const {
        name,
        surname,
        birthDay,
        patronymic,
        hasPatronymic,
        gender,
      } = selectedPatient

      const requestData = {
        patronymic,
        first_name: name,
        last_name: surname,
        birthday: birthDay,
        no_patronymic: !hasPatronymic,
        made_by_other_person: selectedPatient.isOther,
        csrfmiddlewaretoken: getCSRFToken(),
        phone: this.appointmentUserPhone(appointmentDataStore.useAuthMt ? userPhoneV2 : userPhone),
        no_step: 'no_step', // ToDo - удалить при рефакторинге ПЗ и ЭЗ на backend
        source: locationParserReferrer.source,
        profile_uuid: selectedPatient.uuid || '',
        gender,
      }

      if (this.appointmentObjectTypes.isServices) {
        if (serviceDataStore.serviceData.dclass) {
          requestData.service_class = serviceDataStore.serviceData.dclass
        }

        if (!preliminaryConsultationStore.isPreliminaryConsultation) {
          if (doctorDataStore.selectedDoctor.id) {
            requestData.doctor = doctorDataStore.selectedDoctor.id
          }

          if (serviceDataStore.selectedEquipment.id) {
            requestData.equipment = serviceDataStore.selectedEquipment.id
          }
        }

        requestData.lpu = calendarDataStore.initCalendarData.lpuId
      } else if (this.appointmentObjectTypes.isDoctor) {
        Object.assign(requestData, {
          wp: doctorDataStore.selectedWorkplace.id,
          spec: doctorDataStore.selectedDoctorSpecialityId,
        })

        if (
          appointmentDataStore.appointmentPlaceTypes.isTelemed
            || appointmentDataStore.appointmentPlaceTypes.isTelemedMedtochka
        ) {
          requestData.user_timedelta = getTimezoneOffsetInHours()
        }
      }

      if (this.appointmentRegistrationTypes.isDirect && !this.useServiceExpress) {
        const requestDataDirect = {
          mis_comment: appointmentDataStore.selectedLpuCallType,
          slot: calendarDataStore.selectedFullDate,
          is_direct: true,
        }

        if (this.appointmentObjectTypes.isServices) {
          if (this.isAppointmentByReferral) {
            const attachedFilesStore = useAttachedFilesStore()

            requestDataDirect.referral_images = attachedFilesStore.attachedFiles.map(({ file }) => file)
          }

          if (clubDataStore.isClubAvailable) {
            requestDataDirect.type = APPOINTMENT_PLACE_TYPES.club
          }

          requestDataDirect.service_type = serviceDataStore.serviceData.dtype
          requestDataDirect.age = familyProfilesStore.selectedPatient.ageNumber

          if (preliminaryConsultationStore.isPreliminaryConsultation) {
            const {
              selectedConsultingDoctor,
              selectedDoctorConsultation,
            } = preliminaryConsultationStore

            if (selectedConsultingDoctor.id) {
              requestDataDirect.doctor = selectedConsultingDoctor.id
            }

            requestDataDirect.synonym = selectedDoctorConsultation.id
            requestDataDirect.service_requires_consultation_id = calendarDataStore.initCalendarData.synonymId
          } else {
            requestDataDirect.synonym = calendarDataStore.initCalendarData.synonymId
          }
        } else if (this.appointmentObjectTypes.isDoctor) {
          const isPossibleToSelectPaymentTab = doctorDataStore.selectedWorkplace.isInClub
            && this.isClubPossibleOnPage
          const lpuDataStore = useLpuDataStore()

          clubDataStore.clubStatusData.lpuTimedelta = lpuDataStore.lpuData.townTimeDelta
          clubDataStore.clubStatusData.slotDateString = calendarDataStore.selectedSlotDate.toISOString()

          if (placeType && placeType !== APPOINTMENT_PLACE_TYPES.club) {
            requestDataDirect.type = placeType

            if (lpuDataStore.lpuData.telemedContractType) {
              requestDataDirect.telemed_contract_type = lpuDataStore.lpuData.telemedContractType
            }
          } else if (isPossibleToSelectPaymentTab) {
            if (clubDataStore.isClubPaymentTypeSelected) {
              requestDataDirect.type = APPOINTMENT_PLACE_TYPES.club
              yandexGoalsStore.sendYaGoal({
                name: CLUB_YANDEX_GOALS.selectedPaymentTabClub,
                formType: 'anyDirect',
                objectType: 'doctor',
              })
            } else {
              yandexGoalsStore.sendYaGoal({
                name: CLUB_YANDEX_GOALS.selectedPaymentTabLpu,
                formType: 'anyDirect',
                objectType: 'doctor',
              })
            }
          }
        }

        Object.assign(requestData, requestDataDirect)
      } else if (this.appointmentRegistrationTypes.isExpress || this.useServiceExpress) {
        const requestDataExpress = {
          is_express: true,
        }

        // Такая проверка необходима, т.к. сериализуются любые добавленные свойства
        if (calendarDataStore.selectedFullDate) {
          requestDataExpress.slot = calendarDataStore.selectedFullDate
        }

        if (appointmentDataStore.selectedAppointmentCallTime) {
          requestDataExpress.appointment_time = appointmentDataStore.selectedAppointmentCallTime
        }

        if (this.appointmentObjectTypes.isServices) {
          requestDataExpress.age = familyProfilesStore.selectedPatient.ageNumber

          // Такая проверка необходима, т.к. сериализуются любые добавленные свойства
          if (serviceDataStore.serviceData.dtype) {
            requestDataExpress.service_type = serviceDataStore.serviceData.dtype
          }

          if (calendarDataStore.initCalendarData.synonymId) {
            requestDataExpress.synonym = calendarDataStore.initCalendarData.synonymId
          }
        } else if (this.appointmentObjectTypes.isDoctor) {
          if (placeType && placeType !== APPOINTMENT_PLACE_TYPES.club) {
            requestDataExpress.type = placeType
          }
        }

        Object.assign(requestData, requestDataExpress)
      }

      let request

      const formData = new FormData()

      Object.entries(requestData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(i => {
            formData.append(key, i, i.name)
          })
        } else {
          formData.append(key, value)
        }
      })

      if (this.appointmentObjectTypes.isServices) {
        request = requestNewAppointment(NEW_APPOINTMENT_URLS.service, formData)
      } else if (this.appointmentObjectTypes.isDoctor) {
        this.updateStartDateAppointment(performance.now())
        request = requestNewAppointment(NEW_APPOINTMENT_URLS.doctor, formData)
      }

      const globalDataStore = useGlobalDataStore()

      globalDataStore.updateStateSendingUserData({ state: true })

      const handleAppointmentRequest = response => {
        if (this.appointmentObjectTypes.isServices) {
          this.handleAppointmentRequest(response)
        } else if (this.appointmentObjectTypes.isDoctor) {
          if (!this.appointmentPlaceTypes.isTelemed && !this.appointmentPlaceTypes.isTelemedMedtochka) {
            this.handleAppointmentRequest(response)
            return
          }

          const appointmentDuration = performance.now() - this.startDateAppointment

          // Если прошло 4 сек. с начала отображения прелоадера, то производим обработку запроса
          if (appointmentDuration >= PRELOADER_DELAY_FOR_TELEMED_MS) {
            this.handleAppointmentRequest(response)
            return
          }

          // Иначе дожидаемся 4х сек. и производим обработку запроса
          setTimeout(() => {
            this.handleAppointmentRequest(response)
          }, PRELOADER_DELAY_FOR_TELEMED_MS - appointmentDuration)
        }
      }

      request
        .then(handleAppointmentRequest)
        .catch(rejectedData => handleAppointmentRequest(rejectedData.response, rejectedData, requestData))
    },
    handleAppointmentRequest(response = {}) {
      const authorizationStore = useAuthorizationStore() // todo(useAuthMt) - удалить
      const clubDataStore = useClubDataStore()
      const globalDataStore = useGlobalDataStore()
      const yandexGoalsStore = useYandexGoalsStore()
      const preliminaryConsultationStore = usePreliminaryConsultationStore()

      const { data: responseData, status: statusCode } = response

      globalDataStore.updateStateSendingUserData({ state: false })

      if (isNetworkDisconnect({ status: statusCode, message: '' })) {
        globalDataStore.updateErrorData({
          isEnabled: true,
          isDialogRender: true,
          ...APPOINTMENT_ERRORS.notNetwork,
        })
        return
      }

      if (statusCode === 403) {
        globalDataStore.updateErrorData({
          isEnabled: true,
          isDialogRender: true,
          isPersistent: true,
          ...APPOINTMENT_ERRORS.notAuthorization,
        })

        if (!this.useAuthMt) {
          authorizationStore.updateUserActionAfterAuthorization(USER_ACTION_AFTER_AUTH.sendAppointmentForm)
        }

        clubDataStore.updateStateIsClubModalVisible(false)

        return
      }

      if (!responseData) {
        globalDataStore.showDefaultError()

        return
      }

      const {
        window_state: windowState,
        data: appointmentData = {},
      } = responseData
      const {
        coupon_request: coupon,
        call: appointmentCall,
      } = appointmentData

      if (clubDataStore.isClubModalVisible && (statusCode !== 200 || !coupon)) {
        clubDataStore.updateStateIsClubModalVisible(false)
      }

      if (statusCode === 200) {
        if (clubDataStore.isClubModalVisible && coupon) {
          clubDataStore.updateCouponData({
            id: coupon.id,
            dateCreated: coupon.dt_created,
            lifeTime: coupon.LIFETIME_MINUTES,
            lifeTimeSeconds: coupon.coupon_payment_lifetime,
            clubCouponPrice: coupon.club_coupon_price,
            price: coupon.appointment_price,
            fullPrice: coupon.full_appointment_price,
            clubLpuPrice: coupon.club_price_in_lpu,
            cardPrice: coupon.card_price,
            totalPrice: coupon.total_price,
            clubCardLifetimeMonths: coupon.CLUB_CARD_LIFETIME_MONTHS,
          })

          // Сохранить данные экрана успеха для случая, если пользователь нажмёт "Пойду без скидки"
          if (windowState && this.appointmentObjectTypes.isDoctor) {
            globalDataStore.updateSuccessData({
              medtochkaUrl: windowState.medtochka_url,
              title: windowState.title,
              subtitle: windowState.message,
              buttonState: windowState.button_state,
            })
          }

          return
        }

        if (this.appointmentObjectTypes.isDoctor) {
          yandexGoalsStore.sendYaGoal({
            name: 'common_appointment_page__direct_success_message',
            formType: 'anyDirect',
            objectType: 'doctor',
          })
          yandexGoalsStore.sendYaGoal({
            name: APPOINTMENT_YANDEX_GOALS.successDirect,
            formType: 'directClub',
            objectType: 'doctor',
          })
          yandexGoalsStore.sendYaGoal({
            name: APPOINTMENT_YANDEX_GOALS.successDirect,
            formType: 'direct',
            objectType: 'doctor',
          })
          yandexGoalsStore.sendYaGoal({
            name: TOWN_ONLINE_GOALS.redirectedToTelemedPayment,
            formType: 'telemedAndTownOnline',
            objectType: 'doctor',
          })

          if (
            (this.appointmentPlaceTypes.isTelemed || this.appointmentPlaceTypes.isTelemedMedtochka)
            && windowState
            && windowState.medtochka_url
          ) {
            yandexGoalsStore.sendYaGoal({
              name: TELEMED_YANDEX_GOALS.appointmentSuccess,
              formType: 'telemed',
              objectType: 'doctor',
            })
            window.location.assign(windowState.medtochka_url)

            return
          }
        }

        if (appointmentCall && appointmentCall.status === 0) {
          const { call_datetime: callDatetime } = appointmentCall

          globalDataStore.updateSuccessData({
            isEnabled: true,
            title: getSuccessCallTimeTitle(callDatetime),
            subtitle: 'Если пропустите этот звонок, перезвоните по номеру:',
            showPhone: true,
            buttonState: {
              get: 'understandAndAppointmentMore',
              post: 'understandAndAppointmentMore',
            },
          })

          return
        }

        if (appointmentCall && appointmentCall.status >= 0) {
          this.updateAppointmentCall({
            ...appointmentCall,
            isDialogRender: true,
          })
          return
        }

        if (windowState) {
          globalDataStore.updateSuccessData({
            isEnabled: true,
            medtochkaUrl: windowState.medtochka_url,
            title: windowState.title,
            subtitle: windowState.message,
            buttonState: windowState.button_state,
          })

          return
        }

        globalDataStore.updateSuccessDataIsEnabled(true)

        return
      }

      if (!windowState) {
        globalDataStore.showDefaultError()

        return
      }

      const errorData = {
        isDialogRender: true,
        isEnabled: true,
        code: windowState.code,
        title: windowState.title,
        subtitle: windowState.message,
        buttonState: windowState.button_state,
        isPersistent: false,
        redirectUrl: '',
      }

      if (windowState.code === 'patient_in_blacklist') {
        errorData.isDialogRender = false
      } else if (windowState.code === 'simultaneous_appointment') {
        errorData.subtitle = getRemainingTimeMessage(windowState.remaining_time)
      } else if (windowState.code === 'clinic_turn_off_service_appointment') {
        errorData.isDialogRender = false
      } else if (
        windowState.code === 'slot_does_not_exist'
        && this.appointmentObjectTypes.isServices
      ) {
        errorData.buttonState = {
          get: 'reloadClose',
          post: 'reloadClose',
        }
      } else if (windowState.code === 'services_consultation_irrelevant_doctor') {
        // Чтобы ошибка не выводилась дважды, игнорируем единожды её вывод на фронте
        preliminaryConsultationStore.updateIsIgnoreErrorConsultationNotAvailable(true)
      } else if (windowState.code === 'services_consultation_not_single_doctors_left') {
        errorData.isPersistent = true
      } else if (windowState.code === 'has_future_appointment') {
        errorData.buttonState = {
          get: 'understandWithRedirect',
          post: 'understandWithRedirect',
        }
        errorData.redirectUrl = windowState.additional_info.appointment.medtochka_url
      }

      globalDataStore.updateErrorData(errorData)
    },
  },
})

export default useAppointmentDataStore
